// Feedback on an answer: folds, fixes, what went well, the marked-up transcript and diffs.
import { type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';

type FoldProps = { title: ReactNode; children: ReactNode; open?: boolean; onOpen?: () => void };

/** A section that opens and closes. Group several in one Card. */
export function Fold({ title, children, open, onOpen }: FoldProps) {
  return (
    <details className="more" open={open} onToggle={(e) => e.currentTarget.open && onOpen?.()}>
      <summary>
        <span>{title}</span>
        <Icon name="chevron-right" />
      </summary>
      <div>{children}</div>
    </details>
  );
}

type MadeFoldProps = {
  title: ReactNode;
  children: ReactNode;
  /** False until the content exists: opening the fold then makes it. */
  made: boolean;
  make: () => Promise<unknown>;
  /** What shows while it is made, like "Writing your better version…". */
  making: string;
};

/** A fold whose content costs an AI call: made the first time the fold is opened, never before. */
export function MadeFold({ title, children, made, make, making }: MadeFoldProps) {
  const [state, setState] = useState<'waiting' | 'making' | 'failed'>('waiting');
  const start = () => {
    if (made || state === 'making') return;
    setState('making');
    make().then(
      () => setState('waiting'),
      () => setState('failed'),
    );
  };
  return (
    <Fold title={title} onOpen={start}>
      {made ? (
        children
      ) : state === 'failed' ? (
        <div className="stack-sm">
          <p className="t-2 small">That didn't work this time.</p>
          <div>
            <Button size="sm" variant="secondary" onClick={start}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <p className="t-2 small" role="status">
          {making}
        </p>
      )}
    </Fold>
  );
}

export type Fix = { said: string; better: string; why?: string; severity: 'high' | 'medium' | 'low' };

/** What you said (struck through), the better version, and why. */
export function FixItem({ fix }: { fix: Fix }) {
  return (
    <div className="fix">
      <p className="said">"{fix.said}"</p>
      <p className="better">"{fix.better}"</p>
      {fix.why && <p className="why">{fix.why}</p>}
    </div>
  );
}

/** Ticked list: what went well. `next` shows arrows instead, for things to do. */
export function Points({ items, next }: { items: readonly ReactNode[]; next?: boolean }) {
  return (
    <ul className="points">
      {items.map((item, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static feedback lines, never reordered.
        <li key={i}>
          <Icon name={next ? 'arrow-right' : 'check'} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

type Mistake = {
  text: string;
  mark: 'mistake';
  /** The fix that opens when you tap the phrase. */
  better?: string;
  why?: string;
  /** Seconds into the recording, for the numbered mark on the waveform. */
  at?: number;
};

export type TranscriptPart = string | { text: string; mark: 'filler' } | Mistake;

/** The tappable phrase numbered `n` in this transcript. */
const phraseIn = (box: HTMLElement | null, n: number | null) =>
  box?.querySelector<HTMLElement>(`[data-fix="${n}"]`) ?? null;

type TranscriptProps = {
  parts: readonly TranscriptPart[];
  /** The open fix, numbered from 1, when the page shares it (the waveform's marks open fixes too). */
  open?: number | null;
  onOpen?: (n: number | null) => void;
};

/**
 * Your words. Fillers are dotted; mistakes are underlined in amber, and tapping one opens its fix in a small pop-up
 * just under the phrase. Tapping elsewhere or Esc closes it. Mistakes are inline text, not <button>s: a button can't
 * break across lines, so a long mistake would jump to the next line whole and leave a gap.
 */
export function Transcript({ parts, open: shared, onOpen }: TranscriptProps) {
  const [own, setOwn] = useState<number | null>(null);
  const open = shared === undefined ? own : shared;
  const setOpen = onOpen ?? setOwn;
  const box = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const popId = useId();
  const mistakes = parts.filter((p): p is Mistake => typeof p !== 'string' && p.mark === 'mistake');
  const fix = open ? mistakes[open - 1] : undefined;
  let n = 0;

  // Under the last line of the phrase, kept inside the transcript's width.
  useLayoutEffect(() => {
    if (!fix?.better) {
      setPlace(null);
      return;
    }
    const measure = () => {
      const line = [...(phraseIn(box.current, open)?.getClientRects() ?? [])].at(-1);
      if (!line || !box.current || !pop.current) return;
      const outer = box.current.getBoundingClientRect();
      const room = Math.max(0, outer.width - pop.current.offsetWidth);
      setPlace({ top: line.bottom - outer.top + 6, left: Math.min(Math.max(0, line.left - outer.left), room) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, fix]);

  // Opened from the waveform, the phrase may be off screen: bring the fix into view.
  const shown = place ? open : null;
  useEffect(() => {
    if (!shown) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    pop.current?.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
  }, [shown]);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      const target = e.target as Element;
      if (pop.current?.contains(target) || box.current?.contains(target.closest('[data-fix]'))) return;
      setOpen(null);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      phraseIn(box.current, open)?.focus();
      setOpen(null);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open, setOpen]);

  return (
    <div className="stack-sm">
      <div className="transcript-box" ref={box}>
        <p className="transcript">
          {parts.map((p, i) => {
            if (typeof p === 'string') return p;
            if (p.mark === 'filler') {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: transcript parts are positional.
                <mark key={i} className="filler">
                  {p.text}
                </mark>
              );
            }
            n += 1;
            const num = n;
            if (!p.better) {
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: transcript parts are positional.
                <mark key={i}>{p.text}</mark>
              );
            }
            const toggle = () => setOpen(open === num ? null : num);
            return (
              // biome-ignore lint/a11y/useSemanticElements: a <button> can't wrap across lines (see above).
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: transcript parts are positional.
                key={i}
                role="button"
                tabIndex={0}
                className="said"
                data-fix={num}
                aria-expanded={open === num}
                aria-controls={open === num ? popId : undefined}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  toggle();
                }}
              >
                {p.text}
              </span>
            );
          })}
        </p>
        <div aria-live="polite">
          {fix?.better && (
            <div
              ref={pop}
              id={popId}
              className="fix-pop"
              style={place ? { top: place.top, left: place.left } : { visibility: 'hidden' }}
            >
              <b>{fix.better}</b>
              {fix.why && <span className="small t-2">{fix.why}</span>}
            </div>
          )}
        </div>
      </div>
      {mistakes.some((m) => m.better) && <p className="small t-2">Tap an underlined phrase to see the fix.</p>}
    </div>
  );
}

export type DiffPart = string | { del: string } | { ins: string };

/** Your text with removed words struck out and added words highlighted. */
export function Diff({ parts }: { parts: readonly DiffPart[] }) {
  return (
    <p className="diff">
      {parts.map((p, i) => {
        if (typeof p === 'string') return p;
        // biome-ignore lint/suspicious/noArrayIndexKey: diff parts are positional.
        if ('del' in p) return <del key={i}>{p.del}</del>;
        // biome-ignore lint/suspicious/noArrayIndexKey: diff parts are positional.
        return <ins key={i}>{p.ins}</ins>;
      })}
    </p>
  );
}
